<template>
  <div class="form-step">
    <div class="section-header d-flex justify-content-between align-items-center">
      <h5 class="section-title">Grants ({{ localForm.grants.length }})</h5>
      <button type="button" class="btn btn-outline-primary" @click="addGrant">
        <i class="bi bi-plus me-2"></i>Add Grant
      </button>
    </div>

    <div v-for="(grant, index) in localForm.grants" :key="`grant-${grantKey(grant, index)}`" class="grant-item mb-4 p-3 border rounded">
        <div
          class="grant-header d-flex justify-content-between align-items-center"
          role="button"
          tabindex="0"
          @click="toggleGrant(grantKey(grant, index))"
          @keydown.enter.prevent="toggleGrant(grantKey(grant, index))"
          @keydown.space.prevent="toggleGrant(grantKey(grant, index))"
        >
          <h6 class="mb-0 border-0 pb-0">{{ grantHeaderTitles[grantKey(grant, index)] }}</h6>
          <i class="bi" :class="isGrantExpanded(grantKey(grant, index)) ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
        </div>
        <div v-if="isGrantExpanded(grantKey(grant, index))" class="row my-3">
        <div class="col-md-12">
          <label class="form-label">Title</label>
          <input
            type="text"
            class="form-control"
            v-model="grant.title"
            placeholder="Grant title"
            @input="updateParent"
          >
        </div>
      </div>
      <div v-if="isGrantExpanded(grantKey(grant, index))" class="row mb-3">
        <div class="col-md-4">
          <label class="form-label">Code</label>
          <input
            type="text"
            class="form-control"
            v-model="grant.code"
            placeholder="Grant code"
            @input="updateParent"
          >
        </div>
        <div class="col-md-4">
          <label class="form-label">Select role...</label>
          <select
            class="form-select"
            v-model="grant.role"
            @change="updateParent"
          >
            <option value="">Select role...</option>
            <option value="Principal Investigator (PI)">Principal Investigator (PI)</option>
            <option value="Co-Investigator(s)">Co-Investigator(s)</option>
            <option value="Clinical Research Coordinator (CRC)">Clinical Research Coordinator (CRC)</option>
            <option value="Clinical Research Associate (CRA)">Clinical Research Associate (CRA)</option>
            <option value="Data Manager">Data Manager</option>
            <option value="Biostatistician">Biostatistician</option>
            <option value="Regulatory Affairs Specialist">Regulatory Affairs Specialist</option>
            <option value="Clinical Trial Manager">Clinical Trial Manager</option>
            <option value="Clinical Trial Monitor">Clinical Trial Monitor</option>
            <option value="Pharmacist">Pharmacist</option>
            <option value="Patient Recruiter/Enroller">Patient Recruiter/Enroller</option>
            <option value="Quality Assurance Specialist">Quality Assurance Specialist</option>
            <option value="Study Nurse">Study Nurse</option>
            <option value="Other">Other</option>
          </select>
        </div>
        <div class="col-md-4">
          <label class="form-label">Amount</label>
          <input
            type="number"
            class="form-control"
            v-model.number="grant.amount"
            placeholder="0.00"
            step="0.01"
            @input="updateParent"
          >
        </div>
      </div>
      <div v-if="isGrantExpanded(grantKey(grant, index))" class="row mb-3">
        <div class="col-md-12">
          <label class="form-label">Source of funding</label>
          <select
            class="form-select"
            :value="getFundingSourceSelectValue(grant)"
            @change="handleFundingSourceSelectChange(grant, $event.target.value)"
          >
            <option value="">Select funding source</option>
            <option
              v-for="option in fundingSourceOptions"
              :key="option"
              :value="option"
            >
              {{ option }}
            </option>
            <option value="__other__">Other</option>
          </select>
          <input
            v-if="isFundingSourceOther(grant)"
            type="text"
            class="form-control mt-2"
            v-model="grant.fundingSource"
            placeholder="Specify other funding source"
            @input="updateParent"
          >
          <small v-if="isFundingSourceOther(grant)" class="text-muted d-block mt-1">
            Enter the funding source name for the Other option.
          </small>
        </div>
      </div>
      <div v-if="isGrantExpanded(grantKey(grant, index))" class="row mb-3">
        <div class="col-md-4">
          <label class="form-label">Currency</label>
          <select
            class="form-select"
            v-model="grant.currency"
            @change="updateParent"
          >
            <option value="EUR">EUR</option>
            <option value="USD">USD</option>
            <option value="GBP">GBP</option>
          </select>
        </div>
        <div class="col-md-4">
          <label class="form-label">Start year</label>
          <input
            type="number"
            class="form-control"
            v-model.number="grant.startYear"
            :min="1900"
            :max="2100"
            @input="updateParent"
          >
        </div>
        <div class="col-md-4">
          <label class="form-label">End year</label>
          <input
            type="number"
            class="form-control"
            v-model.number="grant.endYear"
            :min="1900"
            :max="2100"
            @input="updateParent"
          >
        </div>
      </div>
      <div v-if="isGrantExpanded(grantKey(grant, index))" class="row">
        <div class="col-md-12 d-flex justify-content-end">
          <button type="button" class="btn btn-sm btn-outline-danger" @click="removeGrant(index)">
            <i class="bi bi-x me-1"></i>Remove
          </button>
        </div>
      </div>
    </div>

    <!-- Show message when no grants -->
    <div v-if="localForm.grants.length === 0" class="text-center py-4 text-muted">
      <i class="bi bi-award fs-1 mb-3"></i>
      <p>No grants added yet. Click the ADD button to add your first grant.</p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'GrantsForm',
  props: {
    formData: {
      type: Object,
      required: true
    },
    profileData: {
      type: Object,
    }
  },
  emits: ['update'],
  data() {
    return {
      localForm: this.initializeLocalForm(),
      expandedGrantIds: [],
      showDebug: false,
      fundingSourceOptions: [
        "Africa Research Excellence Fund (AREF)",
        "African Academy of Sciences (AAS)",
        "Alexander von Humboldt Foundation",
        "amfAR, The Foundation for AIDS Research",
        "ANESVAD Foundation",
        "Arts and Humanities Research Council (AHRC)",
        "AstraZeneca",
        "Belgian Directorate-General for Development Cooperation and Humanitarian Aid (DGD)",
        "Bill & Melinda Gates Foundation (BMGF)",
        "Canadian Institutes of Health Research (CIHR)",
        "Centers for AIDS Research (CFAR)",
        "Centers for Disease Control and Prevention (CDC)",
        "Centre National de la Recherche Scientifique (CNRS)",
        "Coalition for Epidemic Preparedness Innovations (CEPI)",
        "Danish International Development Agency (Danida)",
        "Department for International Development (DFID)",
        "Deutsche Forschungsgemeinschaft / German Research Foundation (DFG)",
        "Dutch Research Council (NWO)",
        "Else Kröner-Fresenius Foundation",
        "European and Developing Countries Clinical Trials Partnership Association (EDCTP2)",
        "European Commission (EC)",
        "European Malaria Vaccine Development Association (EMVDA)",
        "European Union (EU)",
        "FIND",
        "Fondation Mérieux",
        "FONRID",
        "Gauteng Accelerator Program (GAP)",
        "Gavi, the Vaccine Alliance",
        "German Center for Infection Research (DZIF)",
        "German Federal Ministry of Education and Research (BMBF)",
        "GHIT Fund (Global Health Innovative Technology Fund)",
        "Global Challenges Research Fund (GCRF)",
        "Grand Challenges Canada",
        "IMPRINT / Global Challenges Research Fund (GCRF)",
        "Institut Pasteur",
        "Inserm",
        "ICGEB",
        "International Foundation For Science (IFS)",
        "International Society for Infectious Diseases (ISID)",
        "International Union of Basic and Clinical Pharmacology (IUPHAR)",
        "ISP Healthcare",
        "MasterCard Foundation",
        "Medicines for Malaria Venture (MMV)",
        "Mitsubishi Tanabe Pharma Corporation",
        "National Health Laboratory Service South Africa",
        "National Institute for Health Research (NIHR)",
        "National Institutes of Health (NIH)",
        "National Research Foundation",
        "Novartis Pharma",
        "Novo Nordisk",
        "Poliomyelitis Research Foundation",
        "Rockefeller Foundation",
        "Science for Africa Foundation",
        "South African Medical Research Council (SAMRC)",
        "Sida",
        "Swedish Research Council (SRC)",
        "USAID",
        "UK Newton Fund",
        "UK Medical Research Council (MRC)",
        "UK Royal Society (GCRF)",
        "UNICEF",
        "Welcome Trust",
        "WHO / TDR"
      ]
    }
  },
  computed: {
    fellowDetailsGrants() {
      return this.profileData?.data?.user?.fellow_details?.grants || []
    },
    grantHeaderTitles() {
      return (this.localForm.grants || []).reduce((titles, grant, index) => {
        const key = this.grantKey(grant, index)
        const code = typeof grant?.code === 'string' ? grant.code.trim().toUpperCase() : ''
        const title = this.normalizeHeaderText(grant?.title)
        const source = this.normalizeHeaderText(grant?.fundingSource)
        const year = this.getYear(grant?.startYear)

        if (!code && !title && !source && !year) {
          titles[key] = 'New Grant'
          return titles
        }

        const left = [code, title].filter(Boolean).join(' | ')
        const right = [source, year].filter(Boolean).join(', ')
        titles[key] = [left, right].filter(Boolean).join(', ')
        return titles
      }, {})
    }
  },
  watch: {
    formData: {
      handler(newData) {
        this.localForm = this.initializeLocalForm(newData)
        this.initializeExpandedGrantIds()
      },
      deep: true,
      immediate: false // Changed to false to avoid double initialization
    },
    profileData: {
      handler(newData) {
        if (newData) {
          // Only reinitialize if we don't have any grants yet
          if (!this.localForm.grants || this.localForm.grants.length === 0) {
            this.localForm = this.initializeLocalForm(this.formData)
            this.initializeExpandedGrantIds()
          }
        }
      },
      deep: true,
      immediate: false
    }
  },
  methods: {
    initializeLocalForm(data = null) {
      const sourceData = data || this.formData

      // Priority 1: Use existing form data if it has grants
      if (sourceData?.grants && Array.isArray(sourceData.grants) && sourceData.grants.length > 0) {
        return {
          grants: sourceData.grants.map(grant => ({
            id: grant.id || null,
            _localKey: grant.id ? null : `${Date.now()}-${Math.random()}`,
            _isNew: false,
            fundingSource: this.normalizeFundingSource(grant.fundingSource),
            fundingSourceIsOther: this.isCustomFundingSource(this.normalizeFundingSource(grant.fundingSource)),
            title: grant.title || '',
            code: grant.code || '',
            role: this.normalizeGrantRole(grant.role),
            amount: grant.amount || 0,
            currency: grant.currency || 'GBP',
            startYear: grant.startYear || new Date().getFullYear(),
            endYear: grant.endYear || new Date().getFullYear() + 1,
            isEdctpFellowship: grant.isEdctpFellowship || false,
            fellowshipToLink: grant.fellowshipToLink || ''
          }))
        }
      }

      // Priority 2: Extract from API response if available
      const fellowDetailsGrants = this.fellowDetailsGrants
      if (Array.isArray(fellowDetailsGrants) && fellowDetailsGrants.length > 0) {
        return {
          grants: fellowDetailsGrants.map(grant => ({
            id: grant.id || null,
            _localKey: grant.id ? null : `${Date.now()}-${Math.random()}`,
            _isNew: false,
            fundingSource: this.normalizeFundingSource(grant.fundingSource),
            fundingSourceIsOther: this.isCustomFundingSource(this.normalizeFundingSource(grant.fundingSource)),
            title: grant.title || '',
            code: grant.code || '',
            role: grant.role || '',
            amount: grant.amount || 0,
            currency: grant.currency || 'GBP',
            startYear: grant.startYear || new Date().getFullYear(),
            endYear: grant.endYear || new Date().getFullYear() + 1,
            isEdctpFellowship: grant.isEdctpFellowship || false,
            fellowshipToLink: grant.fellowshipToLink || ''
          }))
        }
      }
      return {
        grants: []
      }
    },

    updateParent() {
      const sanitizedGrants = (this.localForm.grants || []).map(({ fundingSourceIsOther, _localKey, _isNew, ...grant }) => ({
        ...grant
      }))

      this.$emit('update', {
        ...this.localForm,
        grants: sanitizedGrants
      })
    },

    addGrant() {
      const newGrant = {
        id: null,
        _localKey: `${Date.now()}-${Math.random()}`,
        _isNew: true,
        title: '',
        code: '',
        role: '',
        amount: 0,
        currency: 'GBP',
        fundingSource: '',
        fundingSourceIsOther: false,
        startYear: new Date().getFullYear(),
        endYear: new Date().getFullYear() + 1,
        isEdctpFellowship: false,
        fellowshipToLink: ''
      }

      this.localForm.grants.push(newGrant)
      const key = this.grantKey(newGrant, this.localForm.grants.length - 1)
      if (key && !this.expandedGrantIds.includes(key)) {
        this.expandedGrantIds.push(key)
      }
      this.updateParent()
    },

    removeGrant(index) {
      const removed = this.localForm.grants[index]
      this.localForm.grants.splice(index, 1)
      const removedKey = this.grantKey(removed, index)
      this.expandedGrantIds = this.expandedGrantIds.filter((id) => id !== removedKey)
      this.updateParent()
    },

    grantKey(grant, index) {
      if (!grant) return `new-${index}`
      return grant.id || grant._localKey || `new-${index}`
    },

    toggleGrant(key) {
      if (!key) return

      if (this.expandedGrantIds.includes(key)) {
        this.expandedGrantIds = this.expandedGrantIds.filter((id) => id !== key)
      } else {
        this.expandedGrantIds.push(key)
      }
    },

    isGrantExpanded(key) {
      return this.expandedGrantIds.includes(key)
    },

    initializeExpandedGrantIds() {
      this.expandedGrantIds = (this.localForm.grants || [])
        .map((grant, index) => (grant._isNew ? this.grantKey(grant, index) : null))
        .filter(Boolean)
    },

    normalizeHeaderText(value) {
      if (typeof value !== 'string') return ''
      const cleaned = value.trim().replace(/\s+/g, ' ')
      if (!cleaned) return ''

      return cleaned
        .toLowerCase()
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    },

    getYear(value) {
      if (value === null || value === undefined) return ''
      const asString = String(value).trim()
      const match = asString.match(/^(\d{4})/)
      return match ? match[1] : ''
    },

    // Helpers //

    normalizeGrantRole(value) {
      if (!value) return "";

      const v = String(value).trim().toLowerCase();

      const map = {
        "1": "Principal Investigator (PI)",
        "2": "Co-Investigator(s)",
        "3": "Clinical Research Coordinator (CRC)",
        "4": "Clinical Research Associate (CRA)",
        "5": "Data Manager",
        "6": "Biostatistician",
        "7": "Regulatory Affairs Specialist",
        "8": "Clinical Trial Manager",
        "9": "Clinical Trial Monitor",
        "10": "Pharmacist",
        "11": "Patient Recruiter/Enroller",
        "12": "Quality Assurance Specialist",
        "13": "Study Nurse",
        "14": "Other"
      };

      // If numeric and exists
      if (map[v]) return map[v];

      // Otherwise return original value if it's already a string label
      return value;
    },

    normalizeFundingSource(value) {
      // 1) No value → empty
      if (!value) return "";

      // 2) If array → return first valid item
      if (Array.isArray(value)) {
        return value.length ? this.normalizeFundingSource(value[0]) : "";
      }

      // 3) If object → try known patterns (value/meta/label)
      if (typeof value === "object") {
        if (value.value) return this.normalizeFundingSource(value.value);
        if (value.meta) return this.normalizeFundingSource(value.meta);
        if (value.label) return this.normalizeFundingSource(value.label);
        return "";
      }

      // 4) Now it's safe to treat it as string
      const v = String(value).trim().toLowerCase();

      // Known alias map
      const aliasMap = {
        "bill and melinda gates foundation": "Bill & Melinda Gates Foundation (BMGF)",
        "bill & melinda gates foundation": "Bill & Melinda Gates Foundation (BMGF)",
        "bmgf": "Bill & Melinda Gates Foundation (BMGF)",
        "gates foundation": "Bill & Melinda Gates Foundation (BMGF)",
        "nih": "National Institutes of Health (NIH)",
        "mrc": "UK Medical Research Council (MRC)",
        "samrc": "South African Medical Research Council (SAMRC)",
        "welcome trust": "Welcome Trust",
        "unicef": "UNICEF",
        "usaid": "U.S. Agency for International Development (USAID)",
        "edctp": "European and Developing Countries Clinical Trials Partnership Association (EDCTP2)",
        "edctp2": "European and Developing Countries Clinical Trials Partnership Association (EDCTP2)",
      };

      if (aliasMap[v]) return aliasMap[v];

      // 5) Fuzzy match canonicals safely
      const canonical = this.getFundingSourceOptions();

      const match = canonical.find(opt => {
        const optLower = opt.toLowerCase();
        return optLower.includes(v) || v.includes(optLower);
      });

      if (match) return match;

      // 6) Fallback: return original clean string
      return value;
    },

    getFundingSourceOptions() {
      return Array.isArray(this.fundingSourceOptions) ? this.fundingSourceOptions : []
    },

    isCustomFundingSource(value) {
      const options = this.getFundingSourceOptions()
      return !!value && !options.includes(value)
    },

    isFundingSourceOther(grant) {
      return !!grant.fundingSourceIsOther || this.isCustomFundingSource(grant.fundingSource)
    },

    getFundingSourceSelectValue(grant) {
      if (!grant?.fundingSource) {
        return grant?.fundingSourceIsOther ? '__other__' : ''
      }

      if (this.getFundingSourceOptions().includes(grant.fundingSource) && !grant.fundingSourceIsOther) {
        return grant.fundingSource
      }

      return '__other__'
    },

    handleFundingSourceSelectChange(grant, selectedValue) {
      if (selectedValue === '__other__') {
        grant.fundingSourceIsOther = true
        if (this.getFundingSourceOptions().includes(grant.fundingSource)) {
          grant.fundingSource = ''
        }
      } else {
        grant.fundingSourceIsOther = false
        grant.fundingSource = selectedValue
      }

      this.updateParent()
    },
  },

  mounted() {
    this.initializeExpandedGrantIds()
  }
}
</script>

<style scoped>
.form-step {
  min-height: 400px;
}
.form-label {
  font-weight: 500;
  margin-bottom: 0.5rem;
}
.form-control,
.form-select {
  border-radius: 6px;
  border: 1px solid #dee2e6;
  padding: 0.75rem;
}
.form-control:focus,
.form-select:focus {
  border-color: #17a2b8;
  box-shadow: 0 0 0 0.2rem rgba(23, 162, 184, 0.25);
}
.grant-item {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
}
h6 {
  color: #495057;
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 0.5rem;
}
.btn-outline-danger {
  border-color: #dc3545;
  color: #dc3545;
}
.btn-outline-danger:hover {
  background-color: #dc3545;
  color: white;
}
.form-check-input {
  margin-top: 0.25rem;
}
.form-check-label {
  margin-left: 0.5rem;
  color: #495057;
}
.text-muted {
  font-size: 0.875rem;
  color: #6c757d !important;
}

.section-header {
  margin: 2rem 0 1.5rem 0;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #17a2b8;
}

.section-title {
  color: #17a2b8;
  font-weight: 600;
  font-size: 1.1rem;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
</style>
